/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { DegreePlan_LearnerAdmissionStatus as LearnerAdmissionStatus } from '__generated__/graphql-types';

import { Button, Dialog } from '@coursera/cds-core';

import EnrollForCredit from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/DialogElements/EnrollForCredit';
import EnrollmentNotAvailable from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/DialogElements/EnrollmentNotAvailable';
import EnrollmentPeriodEnded from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/DialogElements/EnrollmentPeriodEnded';
import Fcnd from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/DialogElements/Fcnd';
import OpenContent from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/DialogElements/OpenContent';
import styles from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/styles';
import { isDegree } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';

import _t from 'i18n!nls/degree-home';

export type CourseEnrollmentStatus = 'open' | 'closed' | 'not available' | 'unknown';

export interface Props {
  enrollmentUrl: string;
  courseSlug: string;
  isStackable: boolean;
  nextEnrollmentPeriod?: string;
  onOkButtonClick: () => void;
  enrollmentStatus: CourseEnrollmentStatus;
  learnerAdmissionStatus: LearnerAdmissionStatus;
}
const EnrollmentDialogContent = ({
  enrollmentUrl,
  courseSlug,
  nextEnrollmentPeriod,
  onOkButtonClick,
  enrollmentStatus,
  isStackable,
  learnerAdmissionStatus,
}: Props) => {
  const okButton = (
    <Dialog.Actions>
      <Button edgeAlign="start" onClick={onOkButtonClick}>
        {_t('OK')}
      </Button>
    </Dialog.Actions>
  );

  switch (enrollmentStatus) {
    case 'open':
      if (isStackable) {
        if (isDegree(learnerAdmissionStatus)) {
          return (
            <Dialog.Content>
              <EnrollForCredit enrollmentUrl={enrollmentUrl} css={styles.modalContentSegmentPadding} />
              <OpenContent courseSlug={courseSlug} css={styles.modalContentSegmentPadding} />
            </Dialog.Content>
          );
        }
        return (
          <Dialog.Content>
            <Fcnd enrollmentUrl={enrollmentUrl} css={styles.modalContentSegmentPadding} />
            <OpenContent courseSlug={courseSlug} css={styles.modalContentSegmentPadding} />
          </Dialog.Content>
        );
      } else {
        if (isDegree(learnerAdmissionStatus)) {
          return (
            <Dialog.Content>
              <EnrollForCredit enrollmentUrl={enrollmentUrl} css={styles.modalContentSegmentPadding} />
            </Dialog.Content>
          );
        }
        return (
          <Dialog.Content>
            <Fcnd enrollmentUrl={enrollmentUrl} css={styles.modalContentSegmentPadding} />
          </Dialog.Content>
        );
      }
    case 'closed':
      if (!nextEnrollmentPeriod) {
        throw new Error('Expected enrollment period');
      }
      if (isStackable) {
        return (
          <Dialog.Content>
            <EnrollmentPeriodEnded
              nextEnrollmentPeriod={nextEnrollmentPeriod}
              css={styles.modalContentSegmentPadding}
            />
            <OpenContent courseSlug={courseSlug} css={styles.modalContentSegmentPadding} />
          </Dialog.Content>
        );
      }
      return (
        <React.Fragment>
          <Dialog.Content>
            <EnrollmentPeriodEnded nextEnrollmentPeriod={nextEnrollmentPeriod} />
          </Dialog.Content>
          {okButton}
        </React.Fragment>
      );

    case 'not available':
      return (
        <React.Fragment>
          <Dialog.Content>
            <EnrollmentNotAvailable />
          </Dialog.Content>
          {okButton}
        </React.Fragment>
      );

    case 'unknown':
      if (isStackable) {
        return (
          <Dialog.Content>
            <OpenContent courseSlug={courseSlug} css={styles.modalContentSegmentPadding} />
          </Dialog.Content>
        );
      }

      return (
        <React.Fragment>
          <Dialog.Content>
            <EnrollmentNotAvailable />
          </Dialog.Content>
          {okButton}
        </React.Fragment>
      );

    default:
      throw new Error('Unexpected enrollment dialog state');
  }
};

export default EnrollmentDialogContent;
